import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import NetworkLogo from '../ui-components/network-logo.component';
import { useUserProfile } from '../../hooks/useUserProfile';
import { ColorType } from '../../assets/colors/colors.g';
import { styles } from '../../styles/styles.util';
import { i18n } from '../../services/translation/i18n';
import { useSelector } from 'react-redux';
import { getTheme } from '../../stores/selectors/theme.selector';
import { SimpleHeader } from '../ui-components/simple-header.component';
import { useAuth } from '../../hooks/useAuth';
import { showSnackBar } from '../../util/toast.utility';
import { useTranslation } from 'react-i18next';
import { LoadingDots } from '../ui-components/loading-dots/loading-dots.component';
import { useLogger } from '../../services/logging/logger-context';

/**
 * AcceptTerms Component
 *
 * Displays a mandatory terms of service acceptance screen for drivers.
 * Users must accept updated terms before continuing to use the application.
 * Features:
 * - Shows updated terms notification with link to full terms
 * - Provides logout option in header via useAuth hook
 * - Handles terms acceptance via updateDriverTerms API through useUserProfile hook
 * - Shows loading state during acceptance
 */
export const AcceptTerms = () => {
  const { updateDriverTerms } = useUserProfile();
  const { t } = useTranslation();
  const theme = useSelector(getTheme);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();
  const location = useLocation();
  const { logger } = useLogger();
  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await updateDriverTerms();
    } catch (error) {
      logger.error('Failed to accept driver terms', error);
      showSnackBar(t('unknown_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsClick = () => {
    window.open('https://chargelab.co/driver-and-site-host-terms#driver', '_blank');
  };

  const handleSignOut = () => {
    auth.signOut(location.pathname);
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <SimpleHeader
          left={
            <Typography
              onClick={handleSignOut}
              sx={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                color: ColorType.GREY6,
                cursor: 'pointer',
              }}
            >
              {i18n.t('logout')}
            </Typography>
          }
          right={<NetworkLogo height='28px' width='auto' maxWidth={128} />}
        />

        <Box
          sx={{
            padding: '24px 24px',
            height: '100%',
          }}
        >
          {/* Header */}
          <Typography
            variant='h4'
            sx={{
              fontFamily: 'Inter',
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '26px',
              color: ColorType.BLACK,
              mb: 2,
            }}
          >
            {i18n.t('updated_terms')}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              color: ColorType.BLACK,
              mb: 4,
            }}
          >
            {t('by_clicking_accept_and_continue_you_agree')}{' '}
            <Typography
              component='span'
              onClick={handleTermsClick}
              sx={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                color: theme.colorPalettes.primary,
                cursor: 'pointer',
              }}
            >
              {t('terms_of_service')}
            </Typography>
            .
          </Typography>

          {/* Accept button */}
          <Button
            variant='contained'
            onClick={handleAccept}
            disabled={isLoading}
            fullWidth
            sx={{
              backgroundColor: theme.colorPalettes.primary,
              color: ColorType.WHITE,
              fontSize: '16px',
              fontWeight: 600,
              height: '48px',
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Inter',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.colorPalettes.primary,
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: ColorType.GREY0,
                color: ColorType.GREY3,
              },
            }}
          >
            {isLoading ? <LoadingDots /> : t('accept_and_continue')}
          </Button>
        </Box>
      </div>
    </div>
  );
};
